import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AppLogo } from "@/components/app/Primitives";
import TID from "@/lib/testIds";
import { LogOut, ChevronDown, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const MobileCtx = React.createContext({ open: false, setOpen: () => {} });
export function useMobileNav() { return React.useContext(MobileCtx); }
export function MobileNavProvider({ children }) {
  const [open, setOpen] = useState(false);
  return <MobileCtx.Provider value={{ open, setOpen }}>{children}</MobileCtx.Provider>;
}

export function Sidebar({ nav, tenantLabel }) {
  const { open, setOpen } = useMobileNav();
  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        data-testid={TID.sidebar}
        className={`
          fixed lg:sticky top-0 left-0 z-50 lg:z-auto
          w-72 lg:w-64 h-screen bg-white border-r border-slate-200
          flex flex-col flex-shrink-0
          transform transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <AppLogo />
          <button
            onClick={() => setOpen(false)}
            className="lg:hidden p-1.5 rounded-md hover:bg-slate-100"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100 text-[11px] uppercase tracking-widest text-slate-400 font-semibold truncate">
          {tenantLabel}
        </div>
        <nav className="flex-1 overflow-y-auto p-3 thin-scroll">
          {nav.map((group, gi) => (
            <div key={gi} className="mb-4">
              {group.label && (
                <div className="px-3 py-1 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                  {group.label}
                </div>
              )}
              {group.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  data-testid={TID.sidebarLink(it.key)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 my-0.5 text-sm rounded-md transition-colors ${
                      isActive ? "bg-[#0052FF] text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  {it.icon && <it.icon size={16} />}
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

export function Topbar({ title, subtitle, right }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { setOpen } = useMobileNav();
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
      <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
        <button
          onClick={() => setOpen(true)}
          className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
          aria-label="Open menu"
          data-testid="mobile-sidebar-toggle"
        >
          <Menu size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-lg sm:text-xl font-bold text-slate-900 truncate">{title}</h1>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {right}
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid={TID.topbarUser}
            className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-md hover:bg-slate-100 text-sm flex-shrink-0"
          >
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center font-heading font-bold text-xs">
              {(user?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </div>
            <div className="hidden md:block text-left">
              <div className="font-semibold text-slate-800 leading-tight">{user?.name}</div>
              <div className="text-xs text-slate-500 capitalize">{(user?.role || "").replace(/_/g, " ")}</div>
            </div>
            <ChevronDown size={14} className="text-slate-400 hidden md:block" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <div className="text-xs text-slate-400 mt-0.5 truncate">{user?.tenant_name}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/guide")}>Platform guide</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={TID.topbarLogout} onClick={logout} className="text-rose-600">
              <LogOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
